import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../../components/Header";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { getVehiclesForCurrentUser, deleteVehicle } from "../../services/vehicleService";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/* ===== Paletas coherentes con el resto ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ===== Widget ===== */
function StatCard({ icon, label, value, palette, highlight = false, isMobile = false }) {
  return (
    <Box
      sx={{
        p: isMobile ? 1.75 : 2,
        borderRadius: "12px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 1 : 1.25,
        minHeight: 76,
        height: "100%",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.95, display: "grid", placeItems: "center" }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
        <Typography
          variant="caption"
          sx={{
            color: palette.label,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Card contenedora ===== */
function Card({ title, palette, children, action }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: action ? "space-between" : "flex-start",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
          {title}
        </Typography>
        {action && <Box sx={{ display: "flex", alignItems: "center" }}>{action}</Box>}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
        {children}
      </Box>
    </Box>
  );
}

/* ===== Estilo DataGrid (altura completa) ===== */
const dgSx = (palette) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  "& .MuiDataGrid-root": {
    border: "none",
    color: palette.text,
    backgroundColor: "transparent",
    height: "100%",
    width: "100%",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${palette.cardBorder}`,
    color: palette.label,
    fontWeight: 700,
  },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${palette.cardBorder}`,
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-overlay": {
    backgroundColor: "transparent",
    color: palette.label,
  },
});

const Vehicles = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const { userId, driverId, type: userType } = useSelector((state) => state.user);

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (vehicleId) => setConfirmId(vehicleId);

  const handleConfirmDelete = async () => {
    if (!confirmId) return;
    try {
      setDeleting(true);
      await deleteVehicle(confirmId);
      setVehicles((prev) => prev.filter((v) => v.id !== confirmId));
      setConfirmId(null);
    } catch (err) {
      console.error("Error al eliminar vehículo:", err);
      setError(err?.message || "No se pudo eliminar el vehículo.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: "model", headerName: "Modelo", flex: 1 },
    { field: "brand", headerName: "Marca", flex: 1 },
    { field: "driverName", headerName: "Conductor", flex: 1 },
    { field: "year", headerName: "Año", flex: 1 },
    { field: "kilometres", headerName: "Km", flex: 1 },
    { field: "licensePlate", headerName: "Matrícula", flex: 1 },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/vehicles/${params.row.id}`)}
            sx={{
              backgroundColor: palette.btn,
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: palette.btnHover },
              whiteSpace: isMobile ? "normal" : "nowrap",
              px: isMobile ? 0.5 : 1.5,
              py: isMobile ? 0.35 : undefined,
              fontSize: isMobile ? "0.78rem" : "0.9rem",
              lineHeight: 1.05,
              minWidth: isMobile ? 70 : undefined,
            }}
          >
            Más detalles
          </Button>
          <IconButton
            aria-label="Eliminar vehículo"
            onClick={() => handleDeleteClick(params.row.id)}
            size="small"
            sx={{
              color: "#ef4444",
              "&:hover": { color: "#dc2626" },
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getVehiclesForCurrentUser({
          userId,
          driverId,
          userType,
        });
        const formatted = (data || []).map((v, i) => ({
          id: v._id || `veh_${i}`,
          model: v.model ?? "—",
          brand: v.brand ?? "—",
          driverName: v.driver
            ? `${v.driver.name || ""} ${v.driver.lastName || ""}`.trim() || "—"
            : "—",
          year: v.year ?? "—",
          kilometres: v.kilometres ?? 0,
          licensePlate: v.licensePlate ?? "—",
        }));
        setVehicles(formatted);
      } catch (error) {
        console.error("Error al cargar vehículos:", error);
        setVehicles([]);
        setError(error.message || "No se pudieron cargar los vehículos.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [driverId, userId, userType]);

  // KPIs
  const totalVehiculos = vehicles.length;
  const kmTotales = vehicles.reduce((acc, v) => acc + (Number(v.kilometres) || 0), 0);
  const edadMedia =
    vehicles.length > 0
      ? Math.round(
          vehicles.reduce((acc, v) => acc + (new Date().getFullYear() - (Number(v.year) || new Date().getFullYear())), 0) /
            vehicles.length
        )
      : 0;

  return (
    <>
    <Box sx={{ m: "20px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Header title="Vehículos" subtitle="Gestión de vehículos registrados" />
      {error && (
        <Typography sx={{ color: palette.accent, mt: 1, mb: 0.5 }}>
          {error}
        </Typography>
      )}

      {/* Widgets */}
      <Box
        sx={{
          mt: 1,
          mb: 1,
          display: "flex",
          gap: 2,
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          alignItems: "stretch",
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <StatCard
            icon={<DirectionsCarIcon />}
            label="Total vehículos"
            value={totalVehiculos}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <StatCard
            icon={<LocalOfferIcon />}
            label="Km acumulados"
            value={kmTotales.toLocaleString("es-ES")}
            palette={palette}
            highlight
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <StatCard
            icon={<CalendarMonthIcon />}
            label="Edad media"
            value={edadMedia}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
      </Box>

      {/* Card + DataGrid a altura completa */}
      <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", overflowX: "hidden" }}>
        <Card
          title="Listado de vehículos"
          palette={palette}
          action={
            <Button
              variant="contained"
              onClick={() => navigate("/vehicles/register")}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              Añadir vehículo
            </Button>
          }
        >
          <Box sx={dgSx(palette)}>
            <DataGrid
              rows={vehicles}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              rowHeight={52}
              columnHeaderHeight={48}
              disableRowSelectionOnClick
              columnVisibilityModel={
                isMobile
                  ? {
                      driverName: false,
                      year: false,
                      kilometres: false,
                      licensePlate: false,
                    }
                  : undefined
              }
              sx={{ height: "100%", width: "100%" }}
            />
          </Box>
        </Card>
      </Box>
    </Box>
    <Dialog
      open={Boolean(confirmId)}
      onClose={() => setConfirmId(null)}
      PaperProps={{
        sx: {
          backgroundColor: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ color: palette.title, fontWeight: 800 }}>
        Eliminar vehículo
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: palette.text }}>
          ¿Seguro que quieres eliminar este vehículo? Esta acción no se puede deshacer.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setConfirmId(null)} sx={{ color: palette.text }}>
          Cancelar
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirmDelete}
          disabled={deleting}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {deleting ? "Eliminando..." : "Eliminar"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Vehicles;
